// frontend/src/ts/protocols/persistence/tauriUploadStore.ts
import { invokeTauriCommand } from '@/common/utils/tauri'
import type {
  GetPendingUploadsArgs,
  StartUploadWorkerConfig, StartDataCollectionConfig,
  UploadStore,
  PickFileOpts,
  EnqueuePickedFileArgs,
} from './uploadStore'
import type {
  PendingUpload, UploadState, UploadSummary,
  DataCollectionStatus, WatcherConfig, WatcherSourceConfig,
} from './uploadTypes'

type RustPendingUpload = {
  id: number
  state: UploadState
  found_path: string
  path: string | null
  filename: string
  file_size: number
  upload_progress: number
  remote_file_id: string | null
  upload_url: string | null
  expires_at: number | null
  attempt_count: number
  last_attempt_at: number | null
  last_error: string | null
  created_at: number
  /** File's filesystem creation time (ms since epoch). Null when the
   *  filesystem doesn't expose it. Used by the UI countdown for
   *  'found'-state rows. */
  source_created_at?: number | null
  /** Per-source min-age gate (seconds) at insert time. */
  min_file_age?: number
  protocol_step_id?: string | null
}

function pendingUploadFromRust(r: RustPendingUpload): PendingUpload {
  return {
    id: r.id,
    state: r.state,
    foundPath: r.found_path,
    path: r.path,
    filename: r.filename,
    fileSize: r.file_size,
    uploadProgress: r.upload_progress,
    remoteFileId: r.remote_file_id,
    attemptCount: r.attempt_count,
    lastAttemptAt: r.last_attempt_at,
    lastError: r.last_error,
    createdAt: r.created_at,
    sourceCreatedAt: r.source_created_at ?? undefined,
    minFileAgeSeconds: r.min_file_age,
    protocolStepId: r.protocol_step_id ?? undefined,
  }
}

type RustWatcherSourceConfig = {
  dir_path: string
  recursive: boolean
  extensions: string[]
  min_file_age_seconds: number
  backup_source_on_copy: boolean
}

type RustWatcherConfig = {
  sources: RustWatcherSourceConfig[]
}

type RustDataCollectionStatus = {
  watcher_running: boolean
  upload_worker_running: boolean
  watcher_config?: RustWatcherConfig | null
}

function watcherSourceConfigFromRust(s: RustWatcherSourceConfig): WatcherSourceConfig {
  return {
    dirPath: s.dir_path,
    recursive: s.recursive,
    extensions: s.extensions,
    minFileAgeSeconds: s.min_file_age_seconds,
    backupSourceOnCopy: s.backup_source_on_copy,
  }
}

function watcherConfigFromRust(c: RustWatcherConfig | null | undefined): WatcherConfig | null {
  if (!c) return null
  return {sources: c.sources.map(watcherSourceConfigFromRust)}
}

function dataCollectionStatusFromRust(s: RustDataCollectionStatus): DataCollectionStatus {
  return {
    watcherRunning: s.watcher_running,
    uploadWorkerRunning: s.upload_worker_running,
    watcherConfig: watcherConfigFromRust(s.watcher_config ?? null),
  }
}

/**
 * Tauri-mode upload store. ALL uploads — watcher-detected, picker-
 * selected, and native drag-drop — flow through the `pending_uploads`
 * queue (commands on the NDC side: `pick_file`, `enqueue_picked_file`,
 * plus the queue-claim worker commands).
 *
 * Wire format: every command in this store sends its payload as
 * `{ args: { ...snake_case } }`. Tauri's top-level camelCase rename
 * applies to command args (none here) but NOT to nested struct fields,
 * so the inner snake_case is what reaches Rust unchanged. Rust uses
 * `#[serde(rename_all = "snake_case", deny_unknown_fields)]` on each
 * `*Args` struct — any JS typo fails loud (Tauri command bind error)
 * instead of silently defaulting.
 */
export class TauriUploadStore implements Omit<UploadStore, 'browserUpload' | 'enqueuePickedFiles'> {
  /**
   * Enqueue multiple picked files (native drag/drop, multi-select
   * picker, etc.). For each file, copy into the persistence dir via
   * the existing `enqueue_picked_file` Rust command — same code path
   * as the singular `enqueuePickedFile`. The worker then streams each
   * local copy to S3. Order of returned ids matches `files`.
   */
  async enqueuePickedFiles(files: EnqueuePickedFileArgs[]): Promise<number[]> {
    const ids: number[] = []
    for (const f of files) {
      const id = await invokeTauriCommand<number>('enqueue_picked_file', {
        args: {
          company_id: f.companyId,
          protocol_id: f.protocolId,
          source_path: f.sourcePath,
          filename_override: f.filenameOverride ?? null,
          protocol_step_id: f.protocolStepId ?? null,
        },
      })
      ids.push(id)
    }
    return ids
  }

  async retryUpload(companyId: string, protocolId: string, id: number): Promise<void> {
    await invokeTauriCommand('retry_upload', {
      args: { company_id: companyId, protocol_id: protocolId, id },
    })
  }

  async retryAllDeadUploads(companyId: string, protocolId: string): Promise<number[]> {
    return await invokeTauriCommand<number[]>('retry_all_dead_uploads', {
      args: { company_id: companyId, protocol_id: protocolId },
    })
  }

  async summary(companyId: string, protocolId: string): Promise<UploadSummary> {
    return await invokeTauriCommand<UploadSummary>('get_pending_uploads_summary', {
      args: { company_id: companyId, protocol_id: protocolId },
    })
  }

  async getPendingUploads(args: GetPendingUploadsArgs): Promise<PendingUpload[]> {
    const rows = await invokeTauriCommand<RustPendingUpload[]>('get_pending_uploads', {
      args: {
        company_id: args.companyId,
        protocol_id: args.protocolId,
        state_filter: args.stateFilter,
        limit: args.limit ?? null,
      },
    })
    return rows.map(pendingUploadFromRust)
  }

  async getDataCollectionStatus(_companyId: string, _protocolId: string): Promise<DataCollectionStatus> {
    // Watcher/worker are process-global singletons — protocol keys are
    // ignored. Empty args struct on the Rust side. Rust serializes the
    // payload with `#[serde(rename_all = "snake_case")]`; the mapper
    // converts to camelCase for the UI/domain shape.
    const status = await invokeTauriCommand<RustDataCollectionStatus>('get_data_collection_status', {
      args: {},
    })
    return dataCollectionStatusFromRust(status)
  }

  async startUploadWorker(args: StartUploadWorkerConfig): Promise<void> {
    await invokeTauriCommand('start_upload_worker', {
      args: {
        company_id: args.companyId,
        protocol_id: args.protocolId,
        upload_request_url: args.uploadRequestUrl,
        subject_id: args.subjectId,
        concurrency: args.concurrency,
      },
    })
  }

  async stopUploadWorker(_companyId: string, _protocolId: string): Promise<void> {
    // Worker is a singleton — `args` is empty on the Rust side.
    await invokeTauriCommand('stop_upload_worker', { args: {} })
  }

  async startDataCollection(args: StartDataCollectionConfig): Promise<void> {
    await invokeTauriCommand('start_data_collection', {
      args: {
        company_id: args.companyId,
        protocol_id: args.protocolId,
        sources: args.sources.map((s) => ({
          dir_path: s.dirPath,
          recursive: s.recursive,
          extensions: s.extensions,
          min_file_age_seconds: s.minFileAgeSeconds,
          backup_source_on_copy: s.backupSourceOnCopy,
        })),
        protocol_step_id: args.protocolStepId ?? null,
      },
    })
  }

  async stopDataCollection(_companyId: string, _protocolId: string): Promise<void> {
    // Watcher is a singleton — `args` is empty on the Rust side.
    await invokeTauriCommand('stop_data_collection', { args: {} })
  }

  async pickFile(opts?: PickFileOpts): Promise<string | null> {
    return await invokeTauriCommand<string | null>('pick_file', {
      args: {
        allowed_extensions: opts?.allowedExtensions ?? null,
      },
    })
  }

  async enqueuePickedFile(args: EnqueuePickedFileArgs): Promise<number> {
    return await invokeTauriCommand<number>('enqueue_picked_file', {
      args: {
        company_id: args.companyId,
        protocol_id: args.protocolId,
        source_path: args.sourcePath,
        filename_override: args.filenameOverride ?? null,
        protocol_step_id: args.protocolStepId ?? null,
      },
    })
  }
}